import { Badge } from "@/components/ui/badge";
import NotificationBell from "@/components/NotificationBell";

export function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 pb-6 border-b border-border">
      <div>
        {eyebrow && <div className="label-caps text-muted-foreground mb-1">{eyebrow}</div>}
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1 text-sm sm:text-base">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 sm:justify-end flex-wrap">
        {children && <div className="flex gap-2 flex-wrap">{children}</div>}
        <div className="hidden lg:block"><NotificationBell /></div>
      </div>
    </div>
  );
}

const statusMap = {
  disponibile: { label: "Disponibile", cls: "bg-success/12 text-success border-success/25" },
  assegnato: { label: "Assegnato", cls: "bg-primary/10 text-primary border-primary/25" },
  manutenzione: { label: "In manutenzione", cls: "bg-warning/12 text-warning border-warning/30" },
  dismesso: { label: "Dismesso", cls: "bg-muted text-muted-foreground border-border" },
  attivo: { label: "Attivo", cls: "bg-success/12 text-success border-success/25" },
  restituito: { label: "Restituito", cls: "bg-muted text-muted-foreground border-border" },
  scaduta: { label: "Scaduta", cls: "bg-destructive/12 text-destructive border-destructive/25" },
  in_scadenza: { label: "In scadenza", cls: "bg-warning/12 text-warning border-warning/30" },
};

export function StatusBadge({ status }) {
  const s = statusMap[status] || { label: status, cls: "bg-muted text-muted-foreground border-border" };
  return (
    <Badge variant="outline" className={`rounded-md font-semibold ${s.cls}`}>
      {s.label}
    </Badge>
  );
}

const typeLabels = { mezzo: "Mezzo", attrezzatura: "Attrezzatura", dispositivo: "Dispositivo" };
export const assetTypeLabel = (t) => typeLabels[t] || t;

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-4">
      {Icon && <Icon className="h-12 w-12 text-muted-foreground/40 mb-4" strokeWidth={1.5} />}
      <h3 className="font-heading font-bold text-lg">{title}</h3>
      {subtitle && <p className="text-muted-foreground text-sm mt-1 max-w-sm">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export const euro = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" }).format(n || 0);

const ROME_TZ = "Europe/Rome";

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const parts = new Intl.DateTimeFormat("it-IT", {
      timeZone: ROME_TZ, day: "2-digit", month: "2-digit", year: "numeric",
    }).formatToParts(new Date(iso));
    const g = (t) => parts.find((x) => x.type === t).value;
    return `${g("day")}-${g("month")}-${g("year")}`;
  } catch {
    return iso;
  }
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  // naive datetime (no timezone designator) = already Rome wall-clock -> show verbatim
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  if (m && !hasTz) return `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}`;
  try {
    const parts = new Intl.DateTimeFormat("it-IT", {
      timeZone: ROME_TZ, day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(iso));
    const g = (t) => parts.find((x) => x.type === t).value;
    return `${g("day")}-${g("month")}-${g("year")} ${g("hour")}:${g("minute")}`;
  } catch {
    return iso;
  }
};

// Current Rome wall-clock as a value for <input type="datetime-local"> (YYYY-MM-DDTHH:mm)
export const nowRomeInput = () => {
  try {
    const parts = new Intl.DateTimeFormat("it-IT", {
      timeZone: ROME_TZ, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date());
    const g = (t) => parts.find((x) => x.type === t).value;
    return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
  } catch {
    return new Date().toISOString().slice(0, 16);
  }
};

export const trackingUnit = (t) => (t === "km" ? "km" : t === "ore" ? "ore" : "");

export function DeadlineBadges({ deadlines }) {
  if (!deadlines || !deadlines.length) return <span className="text-muted-foreground">—</span>;
  const cls = {
    scaduta: "bg-destructive/12 text-destructive border-destructive/25",
    in_scadenza: "bg-warning/12 text-warning border-warning/30",
    ok: "bg-success/12 text-success border-success/25",
  };
  return (
    <div className="flex flex-col gap-1 items-start">
      {deadlines.map((d) => (
        <span key={d.type} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border font-medium ${cls[d.status]}`}>
          {d.type}: {fmtDate(d.due)}
        </span>
      ))}
    </div>
  );
}
